import React, { useEffect, useState } from 'react';
import { parentApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../../components/common/Spinner';
import { FaGraduationCap, FaSchool } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

export default function ParentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [children, setChildren] = useState([]);
  const [notices,  setNotices]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState('');

  useEffect(() => {
    Promise.all([
      parentApi.getChildren().catch(() => ({ data: { data: [] } })),
      parentApi.getNotices().catch(() => ({ data: { data: [] } })),
    ]).then(([c, n]) => {
      setChildren(Array.isArray(c.data?.data) ? c.data.data : []);
      setNotices(Array.isArray(n.data?.data) ? n.data.data.slice(0, 5) : []);
    }).catch(e => {
      setError('Failed to load dashboard data');
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <Spinner />;

  // Group children by school
  const bySchool = children.reduce((acc, c) => {
    const key = c.school_slug;
    if (!acc[key]) acc[key] = { school_name: c.school_name, school_slug: c.school_slug, school_logo: c.school_logo, primary_color: c.primary_color, learners: [] };
    acc[key].learners.push(c);
    return acc;
  }, {});

  return (
    <div>
      <div className="page-header">
        <h2>Welcome, {user?.first_name}! 👋</h2>
        <p>Parent Portal — monitoring {children.length} learner{children.length !== 1 ? 's' : ''} across {Object.keys(bySchool).length} school{Object.keys(bySchool).length !== 1 ? 's' : ''}</p>
      </div>

      {error && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '12px 16px', borderRadius: 8, marginBottom: 16 }}>{error}</div>}

      {/* Children grouped by school */}
      {Object.keys(bySchool).length === 0 ? (
        <div className="sms-card" style={{ textAlign: 'center', padding: '60px 20px', color: '#9ca3af' }}>
          <FaGraduationCap size={48} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p>No children linked to your account yet.</p>
          <p style={{ fontSize: '0.83rem', marginTop: 4 }}>Contact your child's school administrator to get linked.</p>
        </div>
      ) : Object.values(bySchool).map(school => (
        <div key={school.school_slug} style={{ marginBottom: 28 }}>
          {/* School header */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            {school.school_logo
              ? <img src={school.school_logo} alt="" style={{ width: 36, height: 36, borderRadius: 8, objectFit: 'cover' }} />
              : <div style={{ width: 36, height: 36, borderRadius: 8, background: school.primary_color || '#0d6efd', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <FaSchool color="#fff" size={16} />
                </div>
            }
            <h5 style={{ fontWeight: 700, margin: 0, color: '#374151' }}>{school.school_name}</h5>
          </div>

          {/* Learner cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(240px,1fr))', gap: 16 }}>
            {school.learners.map(c => (
              <div key={c.user_id} className="sms-card"
                style={{ cursor: 'pointer', transition: 'box-shadow 0.2s', borderTop: `3px solid ${school.primary_color || '#0d6efd'}` }}
                onClick={() => navigate('/parent/progress', { state: { learnerId: c.user_id, schoolSlug: c.school_slug } })}
                onMouseEnter={e => e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)'}
                onMouseLeave={e => e.currentTarget.style.boxShadow = '0 1px 4px rgba(0,0,0,0.07)'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                  <div style={{ width: 52, height: 52, borderRadius: 12, background: `${school.primary_color || '#0d6efd'}22`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {c.avatar
                      ? <img src={c.avatar} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 12 }} />
                      : <FaGraduationCap size={24} color={school.primary_color || '#0d6efd'} />
                    }
                  </div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: '1rem' }}>{c.first_name} {c.surname}</div>
                    <div style={{ color: '#6b7280', fontSize: '0.82rem' }}>Grade {c.grade_number || '—'}</div>
                    {c.dob && (
                      <div style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
                        {new Date().getFullYear() - new Date(c.dob).getFullYear()} years old
                      </div>
                    )}
                  </div>
                </div>
                <div style={{ marginTop: 14, padding: '8px 12px', background: `${school.primary_color || '#0d6efd'}11`, borderRadius: 8, fontSize: '0.82rem', color: school.primary_color || '#0d6efd', fontWeight: 600, textAlign: 'center' }}>
                  View Progress →
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Notices from all schools */}
      {notices.length > 0 && (
        <div className="sms-card">
          <h5 style={{ fontWeight: 600, marginBottom: 14 }}>Latest Notices</h5>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {notices.map(n => (
              <div key={n.notice_id} style={{ padding: '10px 14px', background: '#f9fafb', borderRadius: 8, borderLeft: `3px solid ${n.is_pinned ? '#f59e0b' : n.primary_color || '#0d6efd'}` }}>
                {n.is_pinned && <div style={{ fontSize: '0.7rem', color: '#f59e0b', fontWeight: 700, marginBottom: 3 }}>📌 PINNED</div>}
                <div style={{ fontWeight: 500 }}>{n.title}</div>
                <div style={{ display: 'flex', gap: 12, marginTop: 4, fontSize: '0.75rem', color: '#9ca3af' }}>
                  <span>{n.school_name}</span>
                  <span>{new Date(n.created_at).toLocaleDateString()}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
